import React from 'react';
import PropTypes from 'prop-types';

import MetaTags from '../metatags';
import { determineCanonical } from '../../../utils/determine-canonical-tag';
import { useSiteConfig } from '../../../hooks/site-config';
import { useSiteMetadata } from '../../../hooks/site-metadata';
import { shouldRedactPixels } from '../../../utils/analytics';

const GatsbyHead = ({
  metaTitle,
  metaDescription,
  pageContent,
  excludeSiteName,
  pageStructuredData = null,
}) => {
  const { title: siteName, defaultMetaImage } = useSiteConfig();
  const { liveSiteURL } = useSiteMetadata();

  const { title, navigation, pageInformation } = pageContent || {};
  const contentfulId = pageContent?.contentful_id;
  const {
    seoTitle,
    seoDescription,
    deindexPage,
    pageThumbnail: thumbnail,
    allowTracking,
  } = pageInformation || {};

  const pageTitle = seoTitle || title;
  const fullPageTitle = excludeSiteName
    ? `${pageTitle}`
    : `${pageTitle} - ${siteName}`;
  const description = seoDescription?.internal?.content;
  const canonicalTag = pageInformation?.canonicalTag;
  const canonical = canonicalTag || determineCanonical(navigation?.path);
  const metaImage = thumbnail?.file ? thumbnail : defaultMetaImage;

  const redactPixels = shouldRedactPixels(
    pageContent?.internal?.type || '',
    navigation?.path,
    allowTracking
  );

  const structuredData = [];

  if (navigation && navigation?.breadcrumbs) {
    const { breadcrumbs } = navigation;

    structuredData.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((breadcrumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: breadcrumb.label,
        item: `${liveSiteURL}${breadcrumb.path}`,
      })),
    });
  }

  if (pageStructuredData) {
    structuredData.push({ ...pageStructuredData });
  }

  return (
    <>
      <MetaTags
        siteName={siteName}
        metaImage={metaImage}
        description={description || metaDescription}
        pageTitle={fullPageTitle || metaTitle}
        deindexPage={deindexPage}
        contentfulId={contentfulId}
        canonicalTag={canonical}
        redactPixels={redactPixels}
      />
      {structuredData.length ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      ) : null}
    </>
  );
};

GatsbyHead.propTypes = {
  pageContent: PropTypes.object,
  metaDescription: PropTypes.string,
  metaTitle: PropTypes.string,
  excludeSiteName: PropTypes.bool,
  pageStructuredData: PropTypes.object,
};

export default GatsbyHead;
